import React, { useEffect, useState } from 'react';
import exact from 'prop-types-exact';
import PropTypes from 'prop-types';
import Icon from '../react/Atoms/Icon';
import {
  DEFAULT_CRITICAL_BACKUP_CURRENCY,
  createTotalCurrencyList,
  currencyCodeAndSymbolPropTypes,
  getInitialCurrencySwitchState,
  updateCurrencySettingsInLocalStorageAndFireCustomEvent,
} from '../../util/currency';
import CUSTOM_EVENT from '../../util/customEvents';

const propTypes = exact({
  // currency is current selected and displayed currency
  currency: currencyCodeAndSymbolPropTypes,
  // 'exchangeRates' array only used to list available currencies to choose from on FE
  exchangeRates: PropTypes.arrayOf(
    PropTypes.shape({
      currency: currencyCodeAndSymbolPropTypes,
      rate: PropTypes.number, // not needed
    })
  ).isRequired,
});

const defaultProps = {
  currency: DEFAULT_CRITICAL_BACKUP_CURRENCY,
};

function CurrencySwitch({
  currency: currencySetInCMS,
  exchangeRates: otherAvailableCurrenciesList,
}) {
  // shape should be {code: string, currency}
  const [selectedCurrency, setSelectedCurrency] = useState(
    getInitialCurrencySwitchState(otherAvailableCurrenciesList, currencySetInCMS)
  );

  // Due to MVC View structure and visual design requiring the select in different areas,
  // two renders of same select is required. So to coordinate on state change, update storage and
  // make custom event to sync across two Currency Switch components mobile & desktop
  // and across local currency state throughout the site
  useEffect(() => {
    updateCurrencySettingsInLocalStorageAndFireCustomEvent(selectedCurrency);
  }, [selectedCurrency]);

  // Listener for currency change in other CurrencySwitch component.
  useEffect(() => {
    const coordinateCurrencyStateAcrossSwitches = (event) => {
      setSelectedCurrency(event.detail);
    };
    window.addEventListener(CUSTOM_EVENT.CURRENCY_CHANGE, coordinateCurrencyStateAcrossSwitches);

    return () => {
      window.removeEventListener(
        CUSTOM_EVENT.CURRENCY_CHANGE,
        coordinateCurrencyStateAcrossSwitches
      );
    };
  }, []);

  const exchangeRateCurrencyList = otherAvailableCurrenciesList.map((e) => e.currency);
  const allCurrenciesNoDuplicates = createTotalCurrencyList(
    currencySetInCMS,
    exchangeRateCurrencyList
  );

  const handleCurrencyChangeSelection = (event) => {
    const newSelectedCurrency = allCurrenciesNoDuplicates.find(
      (currencyInfo) => currencyInfo.code === event.target.value
    );
    if (selectedCurrency) {
      setSelectedCurrency(newSelectedCurrency);
    } else {
      console.error('Could not find selected currency');
    }
  };

  return (
    <>
      <Icon id="currency" blockName="Navigation_globeIcon" />
      <label htmlFor="currency-select" className="visually-hidden">
        Select Currency
      </label>
      <select
        id="currency-select"
        className="Navigation_selectElement"
        onChange={handleCurrencyChangeSelection}
        value={selectedCurrency.code}
        aria-label="Select Currency"
      >
        {allCurrenciesNoDuplicates.map(({ code, symbol }) => (
          <option key={code} value={code} className="Navigation_selectOption">
            {`${code} ${symbol}`}
          </option>
        ))}
      </select>
    </>
  );
}

CurrencySwitch.propTypes = propTypes;
CurrencySwitch.defaultProps = defaultProps;

export default CurrencySwitch;
